import React, { useState } from 'react';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { ReportCard } from '../adminData';

interface YearOption { year: number; cycle: string; }

export default function InternalReports() {
  const { data, loading, error } = useApi(() => API.internalReports());
  const [state, setState] = useState<Record<string, 'idle' | 'generating' | 'done' | 'error'>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Cycle for the year-parameterized workbook. null → follow the program's
  // current cycle (from ProgramSettings) until the admin picks another year.
  const [selectedCycle, setSelectedCycle] = useState<string | null>(null);

  // The program's active cycle drives the page label and every non-parameterized
  // report; it auto-rolls when an admin bumps the year in Admin Settings.
  const currentCycle: string = data?.currentCycle ?? 'FY26';
  const availableYears: YearOption[] = data?.availableYears ?? [];
  // Shared cohort-year pick for the year-parameterized reports (workbook + one-pagers).
  const cohortCycle = selectedCycle ?? currentCycle;
  // Reports that stream a file for the chosen cohort year and get a year selector.
  const yearParameterized = (t: string) => t === 'priority-mapping' || t === 'fi-one-pagers';

  const download = (r: ReportCard, run: () => Promise<unknown>) => {
    setState(s => ({ ...s, [r.id]: 'generating' }));
    setErrors(e => ({ ...e, [r.id]: '' }));
    run()
      .then(() => setState(s => ({ ...s, [r.id]: 'done' })))
      .catch((err: unknown) => {
        setState(s => ({ ...s, [r.id]: 'error' }));
        setErrors(e => ({
          ...e,
          [r.id]: err instanceof Error ? err.message : 'Generation failed. Please try again.',
        }));
      });
  };

  const generate = (r: ReportCard) => {
    if (r.type === 'priority-mapping') {
      // Excel workbook: the backend streams the .xlsx and the browser downloads it.
      download(r, () => API.exportPriorityMapping(cohortCycle));
      return;
    }
    if (r.type === 'fi-one-pagers') {
      // FI One-Pagers deck: backend streams the .pptx. fiIds empty = all eligible FIs.
      download(r, () => API.exportDeck({ type: 'one-pagers', cycle: cohortCycle, fiIds: [] }));
      return;
    }
    setState(s => ({ ...s, [r.id]: 'generating' }));
    setErrors(e => ({ ...e, [r.id]: '' }));
    API.generateReport({ type: r.type, cycle: currentCycle, fis: r.fiSelection ?? [] }).catch(() => {});
    setTimeout(() => setState(s => ({ ...s, [r.id]: 'done' })), 1800);
  };

  const btnLabel = (r: ReportCard) => {
    const st = state[r.id] ?? 'idle';
    const ext = r.type === 'priority-mapping' ? '.xlsx' : '.pptx';
    if (st === 'generating') return '⏳ Generating…';
    if (st === 'done') return `✓ Download ${ext}`;
    if (st === 'error') return '↻ Retry';
    return '⚡ Generate Now';
  };

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !data) return <div className="phil-error">Unable to load reports. {error}</div>;

  const reports: ReportCard[] = data.reports;

  return (
    <>
      <div className="phil-page-heading">Available Reports · {currentCycle}</div>
      <div className="phil-page-sub">
        Generate and download reports based on current application and FI data.
      </div>

      {reports.map(r => (
        <div key={r.id} className="report-card">
          <div className="report-icon">{r.icon}</div>
          <div className="report-body">
            <div className="report-title">{r.title}</div>
            <div className="report-desc">{r.description}</div>
            {r.type === 'fi-one-pagers' && (
              <div className="report-fi-note">
                Generates one slide per sponsoring FI (EIRs and VCs excluded). Per-FI selection coming soon.
              </div>
            )}
          </div>
          <div className="report-right">
            <div className="report-last">Last generated: {r.lastGenerated}</div>
            {yearParameterized(r.type) && availableYears.length > 0 && (
              <label className="report-year-select">
                Cohort year:{' '}
                <select
                  value={cohortCycle}
                  disabled={state[r.id] === 'generating'}
                  onChange={e => setSelectedCycle(e.target.value)}
                >
                  {availableYears.map(y => (
                    <option key={y.year} value={y.cycle}>{y.cycle}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              className={`phil-btn phil-btn-sm ${
                state[r.id] === 'done'
                  ? 'phil-btn-success'
                  : state[r.id] === 'error'
                  ? 'phil-btn-danger'
                  : 'phil-btn-primary'
              }`}
              disabled={state[r.id] === 'generating'}
              onClick={() => generate(r)}
            >
              {btnLabel(r)}
            </button>
            {state[r.id] === 'error' && (
              <div className="report-error" role="alert">⚠ {errors[r.id]}</div>
            )}
          </div>
        </div>
      ))}

      <div className="phil-info-note" style={{ marginTop: 14 }}>
        Reports are generated as PowerPoint decks matching the FIL standard template. Download links appear here upon completion (typically &lt;2 min).
      </div>
    </>
  );
}
